/* pageControls.js — keyboard-and-mouse-class page primitives for NoVA
   ──────────────────────────────────────────────────────────────────────────
   Ported 2026-10-17 from the Tradespace agent (ibkr_api
   trading-dashboard/src/utils/pageControls.js). navigate_app and the glide
   scroll only reach routes and section ids the author named. This module is
   what a visitor can do with a mouse and a keyboard, exposed to the voice
   agent (client tool path in AgentDock / CopilotPanel) and, fire-and-forget,
   to the copilot's chat path (aiMessage.pageControls):

     scrollPage · setZoom · zoomInto · click · typeText · setControl
     pressKey · findOnPage · readElement · hover · highlightElement · goBack

   Targets resolve from (a) an outline handle the screen outline printed next
   to the control — `Save[b2]`, `Lab[t3]`, `Search[f1]` — (b) a visible label,
   or (c) a CSS selector / id. Every primitive returns a plain object the
   model can narrate: { ok, message, … } or { ok:false, error, candidates? }.

   Safety posture (decision agent-page-controls-guard-posture):
     • [data-agent-noclick] on a control, or any [data-agent-guard] ancestor,
       makes it untouchable — the agent can point at it, the person presses.
     • Structural navigation (tabs, links, sort headers, expanders, modal
       close) is always allowed. Any other control whose label reads like an
       irreversible verb (promote, terminate, delete, remove, revoke, suspend,
       deploy, merge, pay, checkout, cancel plan, sign out) is refused unless
       it opts in with [data-agent-click]. Marks are the real guard; the verb
       list is the backstop for pages nobody has swept yet.
     • Every refusal carries a spoken-ready reason.

   Same file in the control plane and the App Kit foundation — edit both.
   The visuals live in pageControls.css, imported by the agent surfaces.   */

import { getOutlineElement } from './screenOutline.js';

const ZOOM_KEY = 'nv.pageZoom';
const ZOOM_MIN = 0.75;
const ZOOM_MAX = 2.0;
export const ZOOM_EVENT = 'nv:zoom-change';
export const PAGE_CONTROL_EVENT = 'nv:agent-page-control';

const AGENT_UI = '.va-panel, .va-launcher, .va-rail, .anr, .cp-panel, .cp-fab, .nv-stage-strip, .nv-stage-ticker, .nv-lens';
const IGNORE = '[data-agent-ignore], [data-nova-ignore], script, style, noscript, template';

const INTERACTIVE = [
  'button', 'a[href]', '[role="button"]', '[role="tab"]', '[role="link"]', '[role="menuitem"]',
  '[role="switch"]', '[role="checkbox"]', '[role="radio"]', 'summary', 'th', '[aria-expanded]',
  'input:not([type="hidden"])', 'select', 'textarea', '[contenteditable="true"]', 'label',
  '[data-agent-click]', 'figure', 'figcaption', 'h1, h2, h3, h4', 'section[id]', 'table', 'img[alt]', '[aria-label]',
].join(', ');

/* Labels that read like an irreversible action. Refused unless the control
   opts in with data-agent-click or is structural navigation. */
export const RISK_VERB_RE = /\b(promote|terminate|delete|remove|revoke|suspend|deploy|merge|pay|checkout|cancel plan|sign out)\b/i;

const norm = (s) => String(s || '').replace(/[▶▸►✕✖×✓✔·•→]/g, ' ').replace(/\s+/g, ' ').trim().toLowerCase();
const text = (el, n = 120) => String(el?.textContent || '').replace(/\s+/g, ' ').trim().slice(0, n);
const win = () => (typeof window !== 'undefined' ? window : null);
const doc = () => win()?.document || null;
const route = () => String(win()?.location?.pathname || '');

function visible(el) {
  if (!el || !el.isConnected) return false;
  try {
    if (el.hidden || el.getAttribute('aria-hidden') === 'true') return false;
    const cs = win().getComputedStyle(el);
    if (cs.display === 'none' || cs.visibility === 'hidden' || (cs.opacity !== '' && Number(cs.opacity) === 0)) return false;
    const r = el.getBoundingClientRect();
    return r.width > 0 || r.height > 0;
  } catch (_) { return false; }
}

function inViewport(el) {
  try {
    const r = el.getBoundingClientRect();
    const h = win().innerHeight || 0; const w = win().innerWidth || 0;
    return r.bottom > 0 && r.top < h && r.right > 0 && r.left < w;
  } catch (_) { return false; }
}

function distanceToViewportCenter(el) {
  try {
    const r = el.getBoundingClientRect();
    const cy = (win().innerHeight || 0) / 2;
    return Math.abs(r.top + r.height / 2 - cy);
  } catch (_) { return 1e9; }
}

/** The label a human would use for this element. */
export function labelOf(el) {
  if (!el) return '';
  const d = doc();
  const aria = el.getAttribute?.('aria-label');
  if (aria) return aria.trim();
  const by = el.getAttribute?.('aria-labelledby');
  if (by && d) { const t = text(d.getElementById(by), 80); if (t) return t; }
  const tag = el.tagName;
  if (tag === 'INPUT' || tag === 'SELECT' || tag === 'TEXTAREA') {
    if (el.id && d) {
      const l = Array.from(d.querySelectorAll('label[for]')).find((x) => x.getAttribute('for') === el.id);
      if (l) return text(l, 80);
    }
    const wrap = el.closest('label');
    if (wrap) { const t = text(wrap, 80).replace(text(el, 80), '').trim(); if (t) return t; }
    return el.getAttribute('placeholder') || el.getAttribute('name') || el.getAttribute('title') || (tag === 'INPUT' ? el.value : '') || '';
  }
  if (tag === 'IMG') return el.getAttribute('alt') || el.getAttribute('title') || '';
  if (tag === 'FIGURE') { const c = el.querySelector('figcaption'); if (c) return text(c, 100); }
  if (tag === 'SECTION') {
    const h = el.querySelector('h1,h2,h3,h4,[role="heading"]');
    if (h) return text(h, 100);
  }
  if (tag === 'TABLE') { const c = el.querySelector('caption'); if (c) return text(c, 100); }
  return text(el, 80) || el.getAttribute?.('title') || el.id || '';
}

function nearestHeading(el) {
  let cur = el;
  while (cur && cur !== doc()?.body) {
    const sec = cur.closest('section, article, dialog, [role="dialog"], .cs-card, .card, .panel');
    if (!sec) break;
    const h = sec.querySelector('h1,h2,h3,h4,[role="heading"], caption');
    if (h) return text(h, 60);
    cur = sec.parentElement;
  }
  return '';
}

/* ── Target resolution ─────────────────────────────────────────────────── */

export function scoreLabel(cand, want) {
  if (!cand || !want) return 0;
  if (cand === want) return 1;
  if (cand.startsWith(want)) return 0.85;
  if (cand.includes(want)) return 0.7;
  const w = want.split(' ').filter(Boolean);
  const hit = w.filter((t) => cand.includes(t)).length;
  return w.length ? (hit / w.length) * 0.55 : 0;
}

/**
 * resolveTarget(ref, { kinds?, scope? })
 *   ref: 'b3' | 'Save' | '#id' | '.cls' | '[data-x]' | 'some-id' | { selector } | Element
 *   → { el, label, how } | { ambiguous: [{label, where}] } | null
 */
export function resolveTarget(ref, { kinds = null, scope = null } = {}) {
  const d = doc();
  if (!d) return null;
  if (ref && typeof ref === 'object' && ref.nodeType === 1) return { el: ref, label: labelOf(ref), how: 'element' };
  if (ref && typeof ref === 'object' && ref.selector) ref = ref.selector;
  const raw = String(ref || '').trim();
  if (!raw) return null;

  // (a) outline handle
  if (/^[bltf]\d{1,3}$/i.test(raw)) {
    const el = getOutlineElement(raw.toLowerCase());
    if (el && el.isConnected) return { el, label: labelOf(el), how: 'handle' };
  }
  // (b) id / selector
  const tryQ = (sel) => { try { return d.querySelector(sel); } catch (_) { return null; } };
  if (/^[#.[]/.test(raw) || /[>:~+]/.test(raw)) {
    const el = tryQ(raw);
    if (el) return { el, label: labelOf(el), how: 'selector' };
  }
  if (/^[\w-]+$/.test(raw)) {
    const el = d.getElementById(raw) || tryQ(`[data-section-id="${raw}"]`) || tryQ(`[name="${raw}"]`);
    if (el) return { el, label: labelOf(el), how: 'id' };
  }
  // (c) label search
  const want = norm(raw);
  // Whole document (header / nav links are prime targets), <main> wins ties.
  const root = scope || d.body;
  const main = d.querySelector('main');
  const sel = kinds || INTERACTIVE;
  const all = Array.from(root.querySelectorAll(sel)).filter((el) => !el.closest(AGENT_UI) && !el.closest(IGNORE) && visible(el));
  const scored = [];
  for (const el of all) {
    const lab = norm(labelOf(el));
    const s = scoreLabel(lab, want);
    if (s >= 0.5) scored.push({ el, lab, s, inView: inViewport(el), inMain: !!main && main.contains(el), dist: distanceToViewportCenter(el) });
  }
  if (!scored.length) return null;
  scored.sort((a, b) => (b.s - a.s) || (Number(b.inView) - Number(a.inView)) || (Number(b.inMain) - Number(a.inMain)) || (a.dist - b.dist));
  const best = scored[0];
  // Same label on 2+ distinct visible controls in view → ask. A label and
  // its control, or a figure and its own image / caption, are ONE thing.
  const related = (a, b) => a.contains(b) || b.contains(a) || (a.tagName === 'LABEL' && a.control === b) || (b.tagName === 'LABEL' && b.control === a);
  const peers = scored.filter((c) => c.s === best.s && c.lab === best.lab && c.el !== best.el && c.inView && !related(c.el, best.el));
  if (best.s === 1 && peers.length && best.inView) {
    return { ambiguous: [best, ...peers].slice(0, 5).map((c) => ({ label: labelOf(c.el), where: nearestHeading(c.el) || (c.inView ? 'on screen' : 'off screen') })) };
  }
  return { el: best.el, label: labelOf(best.el), how: `label:${best.s.toFixed(2)}` };
}

function notFound(ref, what = 'control') {
  return { ok: false, error: `No ${what} matching "${ref}" is on this page. Ask what is on screen, or use the handle printed next to the control in the outline (e.g. "b2").` };
}
function ambiguousResult(ref, amb) {
  return { ok: false, error: `"${ref}" matches ${amb.ambiguous.length} controls on screen — say which one.`, candidates: amb.ambiguous };
}

/* ── Guard ─────────────────────────────────────────────────────────────── */

/** Structural navigation: the element's ROLE / position says "navigation"
 *  (tab, link, sort header, expander, nav container, modal close) — its
 *  label is irrelevant, so a tab called "Deploy" is still a tab. */
export function isStructuralNav(el) {
  if (!el) return false;
  if (el.closest('[data-agent-click]')) return true;
  const role = el.getAttribute('role') || '';
  const tag = el.tagName;
  if (role === 'tab' || role === 'link' || role === 'menuitem') return true;
  if (tag === 'A' && el.getAttribute('href')) return true;
  if (tag === 'SUMMARY' || tag === 'TH' || el.hasAttribute('aria-sort')) return true;
  if (el.closest('thead, [role="tablist"], nav, .cs-tabs, .pagination, .breadcrumb')) return true;
  if (el.hasAttribute('aria-expanded') || el.closest('[aria-expanded]')) return true;
  if (/close|dismiss/i.test(el.getAttribute('aria-label') || '')) return true;
  return false;
}

/**
 * canActuate(el, { action }) → { ok:true } | { ok:false, reason }
 *   action: 'click' | 'type' | 'set' | 'hover'
 */
export function canActuate(el, { action = 'click' } = {}) {
  if (!el) return { ok: false, reason: 'nothing to act on' };
  if (el.closest(AGENT_UI)) return { ok: false, reason: 'that is the agent\'s own panel' };
  if (el.closest('[data-agent-noclick]')) return { ok: false, reason: `"${labelOf(el) || 'that control'}" is operator-only — I can point at it, but you have to press it.` };
  if (el.closest('[data-agent-guard]')) return { ok: false, reason: `"${labelOf(el) || 'that control'}" sits inside a guarded control group — you press it, not me.` };
  if (el.disabled || el.getAttribute('aria-disabled') === 'true') return { ok: false, reason: `"${labelOf(el)}" is disabled right now.` };
  if (action === 'hover') return { ok: true };
  if (action === 'click' && isStructuralNav(el)) return { ok: true };
  if (action === 'type' && (el.type === 'password' || /^cc-/.test(el.getAttribute('autocomplete') || ''))) {
    return { ok: false, reason: `"${labelOf(el) || 'that field'}" is a password or payment field — you type that one.` };
  }
  const lab = norm(labelOf(el));
  if (!el.closest('[data-agent-click]') && RISK_VERB_RE.test(lab)) {
    return { ok: false, reason: `"${labelOf(el)}" reads like an irreversible action — I can take you to it and point, but you press it.` };
  }
  return { ok: true };
}

/* ── Visual feedback ───────────────────────────────────────────────────── */

let _ringTimer = null;
let _ringEl = null;
function ring(el, ms = 1400) {
  try {
    if (_ringEl) _ringEl.classList.remove('nv-pc-ring');
    if (_ringTimer) clearTimeout(_ringTimer);
    _ringEl = el;
    el.classList.add('nv-pc-ring');
    _ringTimer = setTimeout(() => { try { el.classList.remove('nv-pc-ring'); } catch (_) {} }, ms);
  } catch (_) {}
}

function bringIntoView(el, block = 'center') {
  try { if (!inViewport(el)) el.scrollIntoView({ behavior: 'smooth', block, inline: 'nearest' }); } catch (_) {}
}

/* ── Scrolling ─────────────────────────────────────────────────────────── */

function isScrollable(el, axis) {
  if (!el || el === doc()?.documentElement || el === doc()?.body) return false;
  try {
    const cs = win().getComputedStyle(el);
    const ov = axis === 'x' ? cs.overflowX : cs.overflowY;
    if (!/(auto|scroll|overlay)/.test(ov)) return false;
    return axis === 'x' ? el.scrollWidth > el.clientWidth + 8 : el.scrollHeight > el.clientHeight + 8;
  } catch (_) { return false; }
}

function scrollableAncestor(el, axis) {
  let cur = el;
  while (cur && cur !== doc()?.body) {
    if (isScrollable(cur, axis)) return cur;
    cur = cur.parentElement;
  }
  return null;
}

/** The container a "scroll down" means right now: the open lens / dialog
 *  body if there is one, else the page. */
function activeScrollContainer(axis = 'y') {
  const d = doc();
  if (!d) return null;
  const lens = d.querySelector('.nv-lens-scroll');
  if (lens && visible(lens)) return lens;
  const dialogs = Array.from(d.querySelectorAll('[role="dialog"], dialog')).filter((x) => visible(x) && !x.closest(AGENT_UI));
  const top = dialogs[dialogs.length - 1];
  if (top) {
    if (isScrollable(top, axis)) return top;
    const inner = Array.from(top.querySelectorAll('*')).find((x) => isScrollable(x, axis));
    if (inner) return inner;
  }
  if (axis === 'x') {
    const cands = Array.from(d.querySelectorAll('div, section, table')).filter((x) => !x.closest(AGENT_UI) && inViewport(x) && isScrollable(x, 'x'));
    cands.sort((a, b) => b.clientWidth - a.clientWidth);
    if (cands[0]) return cands[0];
  }
  return null; // window
}

function pctOf(container, axis) {
  try {
    if (!container) {
      const w = win(); const d = doc().documentElement;
      const total = axis === 'x' ? d.scrollWidth - w.innerWidth : d.scrollHeight - w.innerHeight;
      const cur = axis === 'x' ? w.scrollX : w.scrollY;
      return total > 0 ? Math.round((cur / total) * 100) : 0;
    }
    const total = axis === 'x' ? container.scrollWidth - container.clientWidth : container.scrollHeight - container.clientHeight;
    const cur = axis === 'x' ? container.scrollLeft : container.scrollTop;
    return total > 0 ? Math.round((cur / total) * 100) : 0;
  } catch (_) { return 0; }
}

/** 'page' | 'half' | 'line' | 600 | '600' | '40%' → px for a view of viewLen. */
export function amountToPx(amount, viewLen) {
  if (typeof amount === 'number') return Math.abs(amount);
  const a = String(amount ?? 'page').trim();
  if (/^\d+(\.\d+)?%$/.test(a)) return viewLen * (parseFloat(a) / 100);
  if (/^\d+$/.test(a)) return Number(a);
  if (a === 'half') return viewLen * 0.5;
  if (a === 'line') return 80;
  return viewLen * 0.85;
}

/**
 * scrollPage({ direction = 'down', amount = 'page', target })
 *   direction: down | up | left | right | top | bottom | start | end
 *   amount:    'page' | 'half' | 'line' | number (px) | '40%'
 *   target:    optional ref — scroll THAT container (table, panel) instead.
 */
export function scrollPage({ direction = 'down', amount = 'page', target = null } = {}) {
  const w = win();
  if (!w) return { ok: false, error: 'no window' };
  const dir = String(direction || 'down').toLowerCase();
  const axis = (dir === 'left' || dir === 'right') ? 'x' : 'y';
  let box = null;
  let scopeLabel = 'the page';
  if (target) {
    const r = resolveTarget(target);
    if (!r) return notFound(target, 'scrollable area');
    if (r.ambiguous) return ambiguousResult(target, r);
    box = isScrollable(r.el, axis) ? r.el : scrollableAncestor(r.el, axis);
    scopeLabel = r.label || 'that area';
    if (!box) {
      bringIntoView(r.el, 'start');
      ring(r.el);
      return { ok: true, message: `Scrolled to ${scopeLabel}.`, target: scopeLabel };
    }
  } else {
    box = activeScrollContainer(axis);
    if (box) scopeLabel = box.matches('.nv-lens-scroll') ? 'the zoomed view' : (labelOf(box) || nearestHeading(box) || 'the open panel');
  }
  const viewLen = box ? (axis === 'x' ? box.clientWidth : box.clientHeight) : (axis === 'x' ? w.innerWidth : w.innerHeight);
  const px = amountToPx(amount, viewLen);
  const opts = (left, top) => ({ left, top, behavior: 'smooth' });
  const maxY = box ? box.scrollHeight : doc().documentElement.scrollHeight;
  const go = (x, y, absolute = false) => {
    try {
      if (box) { if (absolute) box.scrollTo(opts(x, y)); else box.scrollBy(opts(x, y)); }
      else if (absolute) w.scrollTo(opts(x, y)); else w.scrollBy(opts(x, y));
    } catch (_) {}
  };
  switch (dir) {
    case 'top': case 'start': go(0, 0, true); break;
    case 'bottom': case 'end': go(0, maxY, true); break;
    case 'up': go(0, -px); break;
    case 'left': go(-px, 0); break;
    case 'right': go(px, 0); break;
    default: go(0, px); break;
  }
  const result = { ok: true, direction: dir, amount: Math.round(px), scope: scopeLabel };
  // Report the landing position once the smooth scroll settles.
  return new Promise((resolve) => setTimeout(() => {
    const pct = pctOf(box, axis);
    result.positionPct = pct;
    result.message = `Scrolled ${dir} ${scopeLabel === 'the page' ? '' : `inside ${scopeLabel} `}— now ${pct}% ${axis === 'x' ? 'across' : 'down'}${!box ? ' the page' : ''}${pct >= 99 ? ' (at the end)' : pct <= 0 ? ' (at the top)' : ''}.`;
    resolve(result);
  }, 420));
}

/* ── Zoom ──────────────────────────────────────────────────────────────── */

export function clampZoom(z) { return Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, Math.round(z * 20) / 20)); }

/** 'in' | 'out' | 'reset' | 1.25 | '125%' | '125' → absolute level, from `before`. */
export function nextZoomLevel(before, { level = null, delta = null, reset = false } = {}) {
  if (reset || level === 'reset' || level === 'default') return 1;
  if (level === 'in' || level === 'bigger' || level === 'larger') return before + 0.25;
  if (level === 'out' || level === 'smaller') return before - 0.25;
  if (level != null) {
    const n = typeof level === 'string' ? parseFloat(level) : Number(level);
    if (!Number.isFinite(n)) return null;
    return n > 3 ? n / 100 : n; // "125" / "125%" → 1.25
  }
  if (delta != null) {
    const dn = typeof delta === 'string' ? parseFloat(delta) : Number(delta);
    if (!Number.isFinite(dn)) return null;
    return before + (Math.abs(dn) > 3 ? dn / 100 : dn);
  }
  return before;
}

export function getZoom() {
  try {
    const v = parseFloat(doc()?.documentElement?.style?.getPropertyValue('--nv-zoom') || '1');
    return Number.isFinite(v) && v > 0 ? v : 1;
  } catch (_) { return 1; }
}

function applyZoom(level, { persist = true, silent = false } = {}) {
  const d = doc();
  if (!d) return 1;
  const z = clampZoom(level);
  d.documentElement.style.setProperty('--nv-zoom', String(z));
  d.body.classList.toggle('nv-zoomed', Math.abs(z - 1) > 0.001);
  if (persist) { try { win().localStorage.setItem(ZOOM_KEY, String(z)); } catch (_) {} }
  const w = win();
  if (!silent) { try { w.dispatchEvent(new w.CustomEvent(ZOOM_EVENT, { detail: { level: z } })); } catch (_) {} }
  try { w.dispatchEvent(new w.Event('resize')); } catch (_) {}
  return z;
}

/** Re-apply the persisted zoom at boot (runs once on module load in a browser). */
export function applyStoredZoom() {
  try {
    const v = parseFloat(win().localStorage.getItem(ZOOM_KEY) || '1');
    if (Number.isFinite(v) && Math.abs(v - 1) > 0.001) applyZoom(v, { persist: false, silent: true });
  } catch (_) {}
}

/** setZoom({ level?, delta?, reset? }) — level: 1.25 | '125%' | 'in' | 'out' | 'reset'. */
export function setZoom({ level = null, delta = null, reset = false } = {}) {
  if (!doc()) return { ok: false, error: 'no document' };
  const before = getZoom();
  const next = nextZoomLevel(before, { level, delta, reset });
  if (next == null) return { ok: false, error: `zoom level "${level ?? delta}" not understood — use a number like 1.25, "125%", "in", "out" or "reset".` };
  const z = applyZoom(next);
  const result = { ok: true, level: z, percent: Math.round(z * 100), previousPercent: Math.round(before * 100), min: ZOOM_MIN * 100, max: ZOOM_MAX * 100 };
  result.message = z === before
    ? `Zoom is already ${result.percent}%${z >= ZOOM_MAX ? ' (the maximum)' : z <= ZOOM_MIN ? ' (the minimum)' : ''}.`
    : `Page zoom ${result.previousPercent}% → ${result.percent}%.`;
  return result;
}

/* ── Lens (zoom into an image / chart / element) ───────────────────────── */

let _lens = null;
export function closeLens() {
  if (!_lens) return false;
  try { _lens.remove(); } catch (_) {}
  _lens = null;
  try { doc().body.classList.remove('nv-lens-open'); } catch (_) {}
  return true;
}
export const lensOpen = () => !!_lens;

function lensSourceFor(el) {
  const tag = el.tagName;
  if (tag === 'IMG') return { kind: 'img', node: el };
  if (tag === 'CANVAS') return { kind: 'canvas', node: el };
  if (tag === 'svg' || tag === 'SVG') return { kind: 'svg', node: el };
  const img = el.querySelector?.('img');
  if (img && img.getBoundingClientRect().width > 80) return { kind: 'img', node: img };
  const canvas = el.querySelector?.('canvas');
  if (canvas && canvas.width > 80) return { kind: 'canvas', node: canvas };
  const svg = el.querySelector?.('svg');
  if (svg && svg.getBoundingClientRect().width > 80) return { kind: 'svg', node: svg };
  return null;
}

function openLens({ src, caption, fit = 'width', part = 'top' }) {
  const d = doc();
  closeLens();
  const root = d.createElement('div');
  root.className = `nv-lens nv-lens-${fit}`;
  root.setAttribute('role', 'dialog');
  root.setAttribute('aria-label', `Zoomed: ${caption} — ${fit === 'width' ? 'full-width, scrollable' : 'whole view'}`);
  const bar = d.createElement('div');
  bar.className = 'nv-lens-bar';
  bar.setAttribute('data-agent-ignore', '');
  const cap = d.createElement('div'); cap.className = 'nv-lens-cap'; cap.textContent = caption;
  const actions = d.createElement('div'); actions.className = 'nv-lens-actions';
  const mk = (label, on) => { const b = d.createElement('button'); b.type = 'button'; b.className = 'nv-lens-btn'; b.textContent = label; b.addEventListener('click', on); return b; };
  let fitW; let fitP;
  const setFit = (f) => { root.classList.remove('nv-lens-width', 'nv-lens-page'); root.classList.add(`nv-lens-${f}`); fitW.classList.toggle('is-on', f === 'width'); fitP.classList.toggle('is-on', f === 'page'); };
  fitP = mk('Fit', () => setFit('page'));
  fitW = mk('Read', () => setFit('width'));
  const close = mk('✕', () => closeLens());
  close.setAttribute('aria-label', 'Close the zoomed view');
  fitP.classList.toggle('is-on', fit === 'page'); fitW.classList.toggle('is-on', fit === 'width');
  actions.append(fitP, fitW, close);
  bar.append(cap, actions);
  const scroll = d.createElement('div');
  scroll.className = 'nv-lens-scroll';
  scroll.addEventListener('click', (e) => { if (e.target === scroll) closeLens(); });
  src.addEventListener?.('click', () => setFit(root.classList.contains('nv-lens-width') ? 'page' : 'width'));
  scroll.append(src);
  root.append(bar, scroll);
  d.body.append(root);
  d.body.classList.add('nv-lens-open');
  _lens = root;
  const onKey = (e) => { if (e.key === 'Escape') { closeLens(); d.removeEventListener('keydown', onKey); } };
  d.addEventListener('keydown', onKey);
  const settle = () => {
    try {
      const frac = part === 'bottom' ? 1 : part === 'middle' ? 0.5 : 0;
      scroll.scrollTop = Math.max(0, (scroll.scrollHeight - scroll.clientHeight) * frac);
    } catch (_) {}
  };
  if (src.tagName === 'IMG' && !src.complete) src.addEventListener('load', settle, { once: true }); else setTimeout(settle, 30);
  return root;
}

/**
 * zoomInto({ target, fit = 'width', part = 'top', level })
 *   Images / charts / SVGs / canvases → a lens overlay at natural size.
 *   Anything else → bring into view, ring it, and (if asked) raise page zoom.
 */
export function zoomInto({ target, fit = 'width', part = 'top', level = null } = {}) {
  const d = doc();
  if (!d) return { ok: false, error: 'no document' };
  if (!target) return { ok: false, error: 'say what to zoom into — a figure caption, "the chart", a table, or a handle like "b3"' };
  const r = resolveTarget(target);
  if (!r) return notFound(target, 'figure, chart or element');
  if (r.ambiguous) return ambiguousResult(target, r);
  const el = r.el;
  const guard = canActuate(el, { action: 'hover' });
  if (!guard.ok) return { ok: false, error: guard.reason };

  const src = lensSourceFor(el);
  if (src) {
    let node = null;
    const caption = labelOf(el) || labelOf(src.node) || nearestHeading(el) || 'zoomed view';
    try {
      if (src.kind === 'img') { node = d.createElement('img'); node.src = src.node.currentSrc || src.node.src; node.alt = src.node.alt || caption; }
      else if (src.kind === 'canvas') { node = d.createElement('img'); node.src = src.node.toDataURL('image/png'); node.alt = caption; }
      else if (src.kind === 'svg') { node = src.node.cloneNode(true); node.removeAttribute('width'); node.removeAttribute('height'); node.classList.add('nv-lens-svg'); }
    } catch (e) {
      node = null; // tainted canvas etc. — fall through to ring + zoom
    }
    if (node) {
      openLens({ src: node, caption, fit, part });
      return { ok: true, via: 'lens', caption, fit, message: `Zoomed into "${caption}" — ${fit === 'width' ? 'full width, scrolls' : 'whole view'}. Say "close" or press Escape to dismiss.` };
    }
  }

  bringIntoView(el, 'center');
  ring(el, 2000);
  let zoomNote = '';
  if (level != null) { const z = setZoom({ level }); if (z.ok) zoomNote = ` Page zoom is now ${z.percent}%.`; }
  else {
    const w = el.getBoundingClientRect().width;
    if (w && w < (win().innerWidth || 1) * 0.45) zoomNote = ' It is small on screen — say "zoom in" and I will raise the page zoom.';
  }
  return { ok: true, via: 'ring', label: r.label, message: `Centred "${r.label}" on screen.${zoomNote}` };
}

/** highlightElement({ target, hold = 2600 }) — point at something without touching it. */
export function highlightElement({ target, hold = 2600 } = {}) {
  const d = doc();
  if (!d) return { ok: false, error: 'no document' };
  if (!target) return { ok: false, error: 'say what to point at — a label, a heading, or a handle like "b2"' };
  const r = resolveTarget(target);
  if (!r) return notFound(target, 'element');
  if (r.ambiguous) return ambiguousResult(target, r);
  bringIntoView(r.el, 'center');
  ring(r.el, Math.max(600, Math.min(8000, Number(hold) || 2600)));
  const operatorOnly = !!r.el.closest('[data-agent-noclick], [data-agent-guard]');
  return { ok: true, label: r.label, operatorOnly, message: `Pointing at "${r.label}"${operatorOnly ? ' — that one is for the user to press' : ''}.` };
}

/* ── Click / type / set ────────────────────────────────────────────────── */

function stateAfter(el) {
  const s = {};
  const exp = el.getAttribute('aria-expanded'); if (exp != null) s.expanded = exp === 'true';
  const selTab = el.getAttribute('aria-selected'); if (selTab != null) s.selected = selTab === 'true';
  const pressed = el.getAttribute('aria-pressed'); if (pressed != null) s.pressed = pressed === 'true';
  const checked = el.getAttribute('aria-checked'); if (checked != null) s.checked = checked === 'true';
  if (el.tagName === 'DETAILS') s.open = el.open;
  if (el.tagName === 'INPUT' && /checkbox|radio/.test(el.type)) s.checked = el.checked;
  return s;
}

const CLICK_KINDS = 'button, a[href], [role="button"], [role="tab"], [role="link"], [role="menuitem"], [role="switch"], [role="checkbox"], [role="radio"], summary, th, [aria-expanded], label, [data-agent-click], input[type="checkbox"], input[type="radio"], input[type="button"], input[type="submit"]';

/** click({ target }) — one left click on a control, by handle / label / selector. */
export function click({ target } = {}) {
  const d = doc();
  if (!d) return { ok: false, error: 'no document' };
  if (!target) return { ok: false, error: 'say what to click — a button or tab label, or its handle like "b2"' };
  let r = resolveTarget(target, { kinds: CLICK_KINDS });
  if (!r) r = resolveTarget(target);
  if (!r) return notFound(target, 'button, link or tab');
  if (r.ambiguous) return ambiguousResult(target, r);
  let el = r.el;
  // Clicking a heading of a collapsible → its toggle; a label → its control.
  if (/^H[1-6]$/.test(el.tagName)) { const t = el.closest('[aria-expanded], summary, button') || el.querySelector('button, [aria-expanded]'); if (t) el = t; }
  if (el.tagName === 'LABEL' && el.control) el = el.control;
  const guard = canActuate(el, { action: 'click' });
  if (!guard.ok) return { ok: false, error: guard.reason, refused: true, label: labelOf(el) };
  if (!el.closest('.nv-lens')) closeLens();
  bringIntoView(el, 'center');
  ring(el);
  const label = labelOf(el) || r.label;
  const href = el.tagName === 'A' ? el.getAttribute('href') : null;
  const external = href && /^https?:\/\//i.test(href) && !href.startsWith(win().location.origin);
  try { el.focus?.({ preventScroll: true }); } catch (_) {}
  try { el.click(); } catch (e) { return { ok: false, error: `click failed: ${e.message}` }; }
  return new Promise((resolve) => setTimeout(() => {
    const res = { ok: true, label, state: stateAfter(el), route: route() };
    if (href) res.href = href;
    res.message = `Clicked "${label}"${external ? ' (opens an external link)' : ''}${res.state.expanded != null ? ` — now ${res.state.expanded ? 'expanded' : 'collapsed'}` : ''}${res.state.selected ? ' — tab selected' : ''}.`;
    resolve(res);
  }, 250));
}

function setNativeValue(el, value) {
  const w = win();
  const proto = el.tagName === 'TEXTAREA' ? w.HTMLTextAreaElement.prototype : el.tagName === 'SELECT' ? w.HTMLSelectElement.prototype : w.HTMLInputElement.prototype;
  const desc = Object.getOwnPropertyDescriptor(proto, 'value');
  if (desc?.set) desc.set.call(el, value); else el.value = value;
  el.dispatchEvent(new w.Event('input', { bubbles: true }));
  el.dispatchEvent(new w.Event('change', { bubbles: true }));
}

function keyEvents(el, key) {
  const w = win();
  const code = key === 'Enter' ? 13 : key === 'Escape' ? 27 : 0;
  const init = { key, code: key, bubbles: true, cancelable: true, keyCode: code, which: code };
  el.dispatchEvent(new w.KeyboardEvent('keydown', init));
  el.dispatchEvent(new w.KeyboardEvent('keypress', init));
  el.dispatchEvent(new w.KeyboardEvent('keyup', init));
}

/** typeText({ target, text, submit = false, clear = true }) */
export function typeText({ target, text: value, submit = false, clear = true } = {}) {
  const d = doc();
  if (!d) return { ok: false, error: 'no document' };
  if (value == null) return { ok: false, error: 'nothing to type' };
  let el = null;
  if (target) {
    const r = resolveTarget(target, { kinds: 'input:not([type="hidden"]), textarea, select, [contenteditable="true"], label, [role="searchbox"], [role="textbox"]' }) || resolveTarget(target);
    if (!r) return notFound(target, 'text field');
    if (r.ambiguous) return ambiguousResult(target, r);
    el = r.el;
    if (el.tagName === 'LABEL' && el.control) el = el.control;
    if (!/^(INPUT|TEXTAREA)$/.test(el.tagName) && !el.isContentEditable) el = el.querySelector('input:not([type="hidden"]), textarea, [contenteditable="true"]') || el;
  } else {
    const a = d.activeElement;
    if (a && (/^(INPUT|TEXTAREA)$/.test(a.tagName) || a.isContentEditable) && !a.closest(AGENT_UI)) el = a;
    else el = Array.from(d.querySelectorAll('input[type="search"], input[placeholder*="earch" i], input[aria-label*="earch" i], input[type="text"]')).find((x) => visible(x) && !x.closest(AGENT_UI)) || null;
    if (!el) return { ok: false, error: 'no text field is focused or obvious — say which field (label or handle like "f1").' };
  }
  if (!/^(INPUT|TEXTAREA)$/.test(el.tagName) && !el.isContentEditable) return { ok: false, error: `"${labelOf(el)}" is not a text field.` };
  const guard = canActuate(el, { action: 'type' });
  if (!guard.ok) return { ok: false, error: guard.reason, refused: true };
  bringIntoView(el, 'center');
  ring(el);
  try { el.focus({ preventScroll: true }); } catch (_) {}
  const str = String(value);
  try {
    if (el.isContentEditable) {
      if (clear) { el.textContent = ''; }
      d.execCommand?.('insertText', false, str);
      if (!el.textContent.includes(str)) { el.textContent = (clear ? '' : el.textContent) + str; el.dispatchEvent(new (win().Event)('input', { bubbles: true })); }
    } else {
      setNativeValue(el, clear ? str : `${el.value || ''}${str}`);
    }
  } catch (e) { return { ok: false, error: `typing failed: ${e.message}` }; }
  if (submit) {
    keyEvents(el, 'Enter');
    const form = el.form || el.closest('form');
    if (form && typeof form.requestSubmit === 'function' && !el.isContentEditable) { try { form.requestSubmit(); } catch (_) {} }
  }
  const res = { ok: true, field: labelOf(el), value: clear ? str : `${el.value || ''}`, submitted: !!submit };
  res.message = `Typed "${str}" into "${res.field}"${submit ? ' and pressed Enter' : ''}.`;
  return res;
}

/** setControl({ target, value }) — select option / checkbox / switch / radio / range / number. */
export function setControl({ target, value } = {}) {
  const d = doc();
  if (!d) return { ok: false, error: 'no document' };
  if (!target) return { ok: false, error: 'say which control to set' };
  const r = resolveTarget(target, { kinds: 'select, input:not([type="hidden"]), [role="switch"], [role="checkbox"], [role="radio"], [role="combobox"], label, [data-agent-click]' }) || resolveTarget(target);
  if (!r) return notFound(target, 'control');
  if (r.ambiguous) return ambiguousResult(target, r);
  let el = r.el;
  if (el.tagName === 'LABEL' && el.control) el = el.control;
  const guard = canActuate(el, { action: 'set' });
  if (!guard.ok) return { ok: false, error: guard.reason, refused: true };
  const label = labelOf(el) || r.label;
  const settable = el.tagName === 'SELECT' || el.tagName === 'INPUT' || /switch|checkbox|radio/.test(el.getAttribute('role') || '');
  if (!settable) return { ok: false, error: `"${label}" is not a settable control — try click_element.` };
  bringIntoView(el, 'center');
  ring(el);
  const v = value;
  const wantBool = (x) => (typeof x === 'boolean' ? x : /^(on|true|yes|checked|enable|enabled|1)$/i.test(String(x)) ? true : /^(off|false|no|unchecked|disable|disabled|0)$/i.test(String(x)) ? false : null);
  try {
    if (el.tagName === 'SELECT') {
      const want = norm(v);
      const opts = Array.from(el.options);
      const opt = opts.find((o) => norm(o.textContent) === want || norm(o.value) === want) || opts.find((o) => norm(o.textContent).includes(want)) || (/^\d+$/.test(String(v)) ? opts[Number(v)] : null);
      if (!opt) return { ok: false, error: `"${label}" has no option "${v}". Options: ${opts.slice(0, 12).map((o) => text(o, 30)).join(', ')}.` };
      setNativeValue(el, opt.value);
      return { ok: true, control: label, value: text(opt, 40), message: `Set "${label}" to "${text(opt, 40)}".` };
    }
    const role = el.getAttribute('role') || '';
    if ((el.tagName === 'INPUT' && /checkbox|radio/.test(el.type)) || /switch|checkbox|radio/.test(role)) {
      const cur = el.tagName === 'INPUT' ? el.checked : el.getAttribute('aria-checked') === 'true' || el.getAttribute('aria-pressed') === 'true';
      const want = v == null || /^toggle$/i.test(String(v)) ? !cur : wantBool(v);
      if (want == null) return { ok: false, error: `value "${v}" not understood for a toggle — say on, off or toggle.` };
      if (want !== cur || (el.tagName === 'INPUT' && el.type === 'radio' && !cur)) el.click();
      return { ok: true, control: label, value: want ? 'on' : 'off', message: `${want ? 'Turned on' : 'Turned off'} "${label}".` };
    }
    if (el.tagName === 'INPUT') {
      setNativeValue(el, String(v));
      return { ok: true, control: label, value: String(v), message: `Set "${label}" to ${v}.` };
    }
    return { ok: false, error: `"${label}" is not a settable control — try click_element.` };
  } catch (e) { return { ok: false, error: `set failed: ${e.message}` }; }
}

/* ── Keys ──────────────────────────────────────────────────────────────── */

function tabbables() {
  const d = doc();
  return Array.from(d.querySelectorAll('a[href], button, input:not([type="hidden"]), select, textarea, summary, [tabindex]:not([tabindex="-1"]), [contenteditable="true"]'))
    .filter((el) => !el.disabled && visible(el) && !el.closest(AGENT_UI));
}

function closeTopDialog() {
  const d = doc();
  if (closeLens()) return 'zoomed view';
  const dialogs = Array.from(d.querySelectorAll('[role="dialog"], dialog[open]')).filter((x) => visible(x) && !x.closest(AGENT_UI));
  const top = dialogs[dialogs.length - 1];
  if (!top) return null;
  const btn = top.querySelector('[aria-label*="close" i], [aria-label*="dismiss" i], button[title*="close" i]');
  if (btn) { btn.click(); return labelOf(top) || 'dialog'; }
  if (top.tagName === 'DIALOG') { try { top.close(); return labelOf(top) || 'dialog'; } catch (_) {} }
  return null;
}

/**
 * pressKey({ key, shift?, ctrl?, meta?, alt? })
 *   Escape → close lens / top dialog. Enter → activate the focused control.
 *   Tab / Shift+Tab → move focus. Arrows, PageUp/Down, Home, End, Space →
 *   scroll. Anything else is dispatched to the focused element.
 */
export function pressKey({ key, shift = false, ctrl = false, meta = false, alt = false } = {}) {
  const d = doc();
  if (!d) return { ok: false, error: 'no document' };
  const k = String(key || '').trim();
  if (!k) return { ok: false, error: 'which key?' };
  const K = k.length === 1 ? k : k[0].toUpperCase() + k.slice(1).replace(/^(arrow)(\w)/i, (_, a, b) => `${a}${b.toUpperCase()}`);
  const active = d.activeElement && !d.activeElement.closest(AGENT_UI) ? d.activeElement : d.body;
  const KE = win().KeyboardEvent;
  const dispatch = (el) => {
    const init = { key: K, code: K, bubbles: true, cancelable: true, shiftKey: shift, ctrlKey: ctrl, metaKey: meta, altKey: alt };
    el.dispatchEvent(new KE('keydown', init));
    el.dispatchEvent(new KE('keyup', init));
  };
  if (/^(Esc|Escape)$/i.test(K)) {
    const closed = closeTopDialog();
    dispatch(active);
    return { ok: true, key: 'Escape', closed, message: closed ? `Closed the ${closed}.` : 'Nothing open to close.' };
  }
  if (/^Enter$/i.test(K)) {
    const guard = canActuate(active, { action: 'click' });
    if (active !== d.body && !guard.ok) return { ok: false, error: guard.reason, refused: true };
    dispatch(active);
    if (active !== d.body && /^(BUTTON|A|SUMMARY)$/.test(active.tagName)) active.click();
    else if (active.form && typeof active.form.requestSubmit === 'function') { try { active.form.requestSubmit(); } catch (_) {} }
    return { ok: true, key: 'Enter', on: labelOf(active), message: `Pressed Enter${active !== d.body ? ` on "${labelOf(active)}"` : ''}.` };
  }
  if (/^Tab$/i.test(K)) {
    const list = tabbables();
    if (!list.length) return { ok: false, error: 'nothing focusable on the page' };
    const i = list.indexOf(active);
    const next = list[(i < 0 ? (shift ? list.length - 1 : 0) : (i + (shift ? -1 : 1) + list.length) % list.length)];
    try { next.focus({ preventScroll: false }); } catch (_) {}
    ring(next, 900);
    return { ok: true, key: shift ? 'Shift+Tab' : 'Tab', focused: labelOf(next), message: `Focus is on "${labelOf(next)}".` };
  }
  const scrollMap = { ArrowDown: ['down', 'line'], ArrowUp: ['up', 'line'], ArrowLeft: ['left', 'line'], ArrowRight: ['right', 'line'], PageDown: ['down', 'page'], PageUp: ['up', 'page'], Home: ['top', 'page'], End: ['bottom', 'page'], ' ': [shift ? 'up' : 'down', 'page'], Space: [shift ? 'up' : 'down', 'page'] };
  const nav = scrollMap[K];
  if (nav && (active === d.body || !/^(INPUT|TEXTAREA|SELECT)$/.test(active.tagName))) {
    return scrollPage({ direction: nav[0], amount: nav[1] });
  }
  dispatch(active);
  return { ok: true, key: K, on: labelOf(active), message: `Pressed ${K}${active !== d.body ? ` on "${labelOf(active)}"` : ''}.` };
}

/* ── Find on page ──────────────────────────────────────────────────────── */

let _findEls = [];
export function clearFind() {
  try { win().CSS?.highlights?.delete('nv-find'); } catch (_) {}
  for (const el of _findEls) { try { el.classList.remove('nv-pc-find'); } catch (_) {} }
  _findEls = [];
}

/**
 * findOnPage({ query, nth = 1, highlight = true })
 *   Ctrl-F: counts matches in the page text (agent UI excluded), highlights
 *   them (CSS Custom Highlight API, underline fallback), scrolls to the nth.
 */
export function findOnPage({ query, nth = 1, highlight = true } = {}) {
  const d = doc();
  if (!d) return { ok: false, error: 'no document' };
  const q = String(query || '').trim();
  if (!q) return { ok: false, error: 'what should I look for?' };
  clearFind();
  const w = win();
  const scope = d.querySelector('main') || d.body;
  const walker = d.createTreeWalker(scope, w.NodeFilter.SHOW_TEXT, {
    acceptNode: (n) => {
      const p = n.parentElement;
      if (!p || p.closest(AGENT_UI) || p.closest(IGNORE)) return w.NodeFilter.FILTER_REJECT;
      if (!n.nodeValue || !n.nodeValue.trim()) return w.NodeFilter.FILTER_SKIP;
      return w.NodeFilter.FILTER_ACCEPT;
    },
  });
  const needle = q.toLowerCase();
  const hits = [];
  let node;
  while ((node = walker.nextNode()) && hits.length < 200) {
    const hay = node.nodeValue.toLowerCase();
    let idx = hay.indexOf(needle);
    while (idx >= 0 && hits.length < 200) {
      const range = d.createRange();
      range.setStart(node, idx); range.setEnd(node, idx + needle.length);
      const s = Math.max(0, idx - 40); const e = Math.min(node.nodeValue.length, idx + needle.length + 40);
      hits.push({ range, el: node.parentElement, snippet: `${s > 0 ? '…' : ''}${node.nodeValue.slice(s, e).replace(/\s+/g, ' ')}${e < node.nodeValue.length ? '…' : ''}` });
      idx = hay.indexOf(needle, idx + needle.length);
    }
  }
  if (!hits.length) return { ok: true, query: q, count: 0, message: `"${q}" is not on this page.` };
  const visibleHits = hits.filter((h) => visible(h.el));
  const list = visibleHits.length ? visibleHits : hits;
  const i = Math.min(Math.max(1, Number(nth) || 1), list.length) - 1;
  const hit = list[i];
  if (highlight) {
    try {
      if (w.CSS?.highlights && typeof w.Highlight === 'function') {
        w.CSS.highlights.set('nv-find', new w.Highlight(...list.map((h) => h.range)));
      }
    } catch (_) {}
    for (const h of list.slice(0, 40)) { try { h.el.classList.add('nv-pc-find'); } catch (_) {} }
    _findEls = list.slice(0, 40).map((h) => h.el);
  }
  try { hit.el.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch (_) {}
  ring(hit.el, 2200);
  const section = nearestHeading(hit.el) || null;
  return {
    ok: true, query: q, count: list.length, shown: i + 1, section,
    snippets: list.slice(0, 5).map((h) => h.snippet),
    message: `Found "${q}" ${list.length} time${list.length === 1 ? '' : 's'}; showing #${i + 1}${section ? ` under "${section}"` : ''}: ${hit.snippet}`,
  };
}

/* ── Read element ──────────────────────────────────────────────────────── */

function tableToText(tbl, maxChars) {
  const rows = Array.from(tbl.querySelectorAll('tr'));
  const out = [];
  let len = 0;
  for (const tr of rows) {
    const cells = Array.from(tr.querySelectorAll('th, td')).map((c) => text(c, 40)).filter(Boolean);
    if (!cells.length) continue;
    const line = cells.join(' | ');
    if (len + line.length > maxChars) { out.push(`… (${rows.length - out.length} more rows)`); break; }
    out.push(line); len += line.length + 1;
  }
  return out.join('\n');
}

/** readElement({ target, maxChars = 1500 }) — the text of a table / section / dialog / figure, verbatim. */
export function readElement({ target, maxChars = 1500 } = {}) {
  const d = doc();
  if (!d) return { ok: false, error: 'no document' };
  const max = Math.max(200, Math.min(6000, Number(maxChars) || 1500));
  let el = null;
  let label = '';
  if (!target || /^(this|screen|page|viewport|what.*see)$/i.test(String(target))) {
    // The section with the biggest share of the viewport.
    let best = null; let bestR = 0;
    for (const s of Array.from(d.querySelectorAll('section, article, .cs-card, .card'))) {
      if (s.closest(AGENT_UI) || !visible(s)) continue;
      const r = s.getBoundingClientRect(); const h = win().innerHeight || 1;
      const vis = Math.max(0, Math.min(r.bottom, h) - Math.max(r.top, 0));
      if (vis > bestR) { bestR = vis; best = s; }
    }
    el = best || d.querySelector('main') || d.body;
    label = labelOf(el) || 'what is on screen';
  } else {
    const r = resolveTarget(target, { kinds: 'table, section[id], [role="dialog"], dialog, figure, figcaption, article, .cs-card, .card, h1, h2, h3, h4, caption, [aria-label]' }) || resolveTarget(target);
    if (!r) return notFound(target, 'table, section or element');
    if (r.ambiguous) return ambiguousResult(target, r);
    el = r.el; label = r.label;
    if (/^H[1-6]$/.test(el.tagName)) el = el.closest('section, article, .cs-card, .card') || el.parentElement;
  }
  const tbl = el.tagName === 'TABLE' ? el : el.querySelector?.('table');
  let body;
  if (tbl && (el === tbl || (tbl.getBoundingClientRect().height > el.getBoundingClientRect().height * 0.5))) body = tableToText(tbl, max);
  else {
    const clone = el.cloneNode(true);
    for (const x of Array.from(clone.querySelectorAll(`${AGENT_UI}, ${IGNORE}, input, select, textarea`))) { try { x.remove(); } catch (_) {} }
    body = String(clone.innerText || clone.textContent || '').replace(/[ \t]+\n/g, '\n').replace(/\n{3,}/g, '\n\n').trim();
    if (body.length > max) body = `${body.slice(0, max)}… (${body.length - max} more chars)`;
  }
  bringIntoView(el, 'start');
  ring(el, 1600);
  return { ok: true, label, chars: body.length, text: body, message: `Reading "${label}":\n${body}` };
}

/* ── Hover ─────────────────────────────────────────────────────────────── */

let _hovered = null;
function unhover() {
  if (!_hovered) return;
  try {
    const w = win();
    for (const type of ['pointerout', 'mouseout', 'pointerleave', 'mouseleave']) {
      const Ctor = type.startsWith('pointer') ? (w.PointerEvent || w.MouseEvent) : w.MouseEvent;
      _hovered.dispatchEvent(new Ctor(type, { bubbles: !type.endsWith('leave'), cancelable: true }));
    }
  } catch (_) {}
  _hovered = null;
}

/** hover({ target, holdMs = 6000 }) — move the pointer over something and report any tooltip. */
export function hover({ target, holdMs = 6000 } = {}) {
  const d = doc();
  if (!d) return { ok: false, error: 'no document' };
  if (!target) return { ok: false, error: 'say what to hover over' };
  const r = resolveTarget(target);
  if (!r) return notFound(target, 'element');
  if (r.ambiguous) return ambiguousResult(target, r);
  const el = r.el;
  const guard = canActuate(el, { action: 'hover' });
  if (!guard.ok) return { ok: false, error: guard.reason };
  unhover();
  bringIntoView(el, 'center');
  const w = win();
  const rect = el.getBoundingClientRect();
  const init = { bubbles: true, cancelable: true, clientX: rect.left + rect.width / 2, clientY: rect.top + rect.height / 2, view: w };
  const PE = w.PointerEvent || w.MouseEvent;
  try {
    el.dispatchEvent(new PE('pointerover', init));
    el.dispatchEvent(new PE('pointerenter', { ...init, bubbles: false }));
    el.dispatchEvent(new w.MouseEvent('mouseover', init));
    el.dispatchEvent(new w.MouseEvent('mouseenter', { ...init, bubbles: false }));
    el.dispatchEvent(new PE('pointermove', init));
    el.dispatchEvent(new w.MouseEvent('mousemove', init));
  } catch (e) { return { ok: false, error: `hover failed: ${e.message}` }; }
  _hovered = el;
  ring(el, 1200);
  setTimeout(unhover, holdMs);
  return new Promise((resolve) => setTimeout(() => {
    const tip = Array.from(d.querySelectorAll('[role="tooltip"], .tooltip, .recharts-tooltip-wrapper')).filter(visible).map((t) => text(t, 300)).filter(Boolean)[0]
      || el.getAttribute('title') || el.getAttribute('data-tooltip') || el.getAttribute('aria-description') || '';
    resolve({ ok: true, label: r.label, tooltip: tip || null, message: tip ? `Hovering "${r.label}" — tooltip: ${tip}` : `Hovering "${r.label}" — no tooltip appeared.` });
  }, 380));
}

/* ── History ───────────────────────────────────────────────────────────── */

/** goBack({ steps = 1 }) — negative steps go forward. */
export function goBack({ steps = 1 } = {}) {
  const w = win();
  if (!w) return { ok: false, error: 'no window' };
  const n = Number(steps) || 1;
  const from = route();
  closeLens();
  try { w.history.go(-n); } catch (e) { return { ok: false, error: `history failed: ${e.message}` }; }
  return new Promise((resolve) => setTimeout(() => {
    const to = route();
    resolve({ ok: true, direction: n > 0 ? 'back' : 'forward', from, to, changed: from !== to, message: from !== to ? `Went ${n > 0 ? 'back' : 'forward'} to ${to}.` : `Nothing to go ${n > 0 ? 'back' : 'forward'} to.` });
  }, 350));
}

/* ── Dispatcher ────────────────────────────────────────────────────────── */

export const PAGE_CONTROL_TOOL_NAMES = ['scroll_page', 'set_zoom', 'zoom_into', 'click_element', 'type_text', 'set_control', 'press_key', 'find_on_page', 'read_element', 'hover_element', 'highlight_element', 'go_back'];

/** Tools that operate INSIDE an open lens and must not close it. */
export const READER_SAFE_TOOLS = new Set(['scroll_page', 'set_zoom', 'press_key', 'find_on_page', 'read_element', 'hover_element', 'highlight_element']);

export function isPageControlTool(name) { return PAGE_CONTROL_TOOL_NAMES.includes(name); }

/** Tools that move or change the page — the agent is "driving" (stage mode). */
export const DRIVING_TOOLS = new Set(['scroll_page', 'set_zoom', 'zoom_into', 'click_element', 'type_text', 'set_control', 'find_on_page', 'highlight_element', 'go_back', 'navigate_app']);

/** The target argument under whichever name the model used. */
export const targetOf = (a) => a?.target ?? a?.element ?? a?.label ?? a?.handle ?? a?.selector ?? a?.id;

/** Run one page-control tool by name. May return a Promise. */
export function runPageControl(name, args = {}) {
  const a = args && typeof args === 'object' ? args : {};
  const tgt = targetOf(a);
  if (lensOpen() && !READER_SAFE_TOOLS.has(name)) closeLens();
  switch (name) {
    case 'scroll_page': return scrollPage({ direction: a.direction, amount: a.amount, target: tgt ?? a.container });
    case 'set_zoom': return setZoom({ level: a.level ?? a.percent, delta: a.delta, reset: !!a.reset });
    case 'zoom_into': return zoomInto({ target: tgt, fit: a.fit, part: a.part, level: a.level });
    case 'click_element': return click({ target: tgt });
    case 'type_text': return typeText({ target: tgt, text: a.text ?? a.value, submit: !!(a.submit ?? a.enter), clear: a.clear !== false });
    case 'set_control': return setControl({ target: tgt, value: a.value });
    case 'press_key': return pressKey({ key: a.key, shift: !!a.shift, ctrl: !!a.ctrl, meta: !!a.meta, alt: !!a.alt });
    case 'find_on_page': return findOnPage({ query: a.query ?? a.text, nth: a.nth, highlight: a.highlight !== false });
    case 'read_element': return readElement({ target: tgt, maxChars: a.max_chars ?? a.maxChars });
    case 'hover_element': return hover({ target: tgt });
    case 'highlight_element': return highlightElement({ target: tgt, hold: a.hold });
    case 'go_back': return goBack({ steps: a.steps ?? (a.forward ? -1 : 1) });
    default: return { ok: false, error: `unknown page control: ${name}` };
  }
}

/* Browser bridge: window.dispatchEvent(new CustomEvent('nv:agent-page-control',
   { detail: { name, args, onResult } })) runs a primitive from anywhere (a
   smoke test, a page with its own reason to drive). Installed once. */
let _bridge = false;
export function installPageControlBridge() {
  const w = win();
  if (!w || _bridge) return;
  _bridge = true;
  w.addEventListener(PAGE_CONTROL_EVENT, (e) => {
    const { name, args, onResult } = e?.detail || {};
    Promise.resolve().then(() => runPageControl(name, args)).then(
      (res) => { if (typeof onResult === 'function') onResult(res); },
      (err) => { if (typeof onResult === 'function') onResult({ ok: false, error: String(err?.message || err) }); },
    );
  });
}

if (typeof window !== 'undefined') {
  try { applyStoredZoom(); } catch (_) {}
  installPageControlBridge();
}
